import { useState, useEffect, useRef, useContext } from 'react';
import { Auth }             from '../../StateManagment/Auth';
import { db }               from '../../firebase/config';
import Location             from '../../hooks/Location';
import buildAccountList     from '../../helpers/switchAccount/buildAccountList';
import recordLogin          from '../../helpers/auth/recordLogin';

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/**
 * Keuzelijst waarmee je van organisatie wisselt. Staat op de regel onder je
 * gebruikersnaam in de topbalk en toont daar de naam van de organisatie waarin
 * je nu werkt.
 *
 * @param {object} props
 * @param {string} [props.fallbackName] - Naam die de aanroeper al kent (uit de
 *   instellingen). Wordt getoond zolang de eigen lijst nog laadt of niet geladen
 *   kon worden — deze regel is de enige plek waar de organisatienaam staat.
 */
const SwitchAccount = ({ fallbackName }) => {
  const [authO]              = useContext(Auth);
  const id                   = Location()[1];
  const compagnies           = authO?.Compagny || [];
  // Superadmins kiezen uit ALLE omgevingen, niet alleen die waar ze lid van
  // zijn — met een tijdelijke sessie komen ze overal binnen.
  // AI-CONTEXT: See docs/features/superadmin-tenant-access.md
  const isSuperAdmin         = authO?.SuperAdmin === true;
  // Stable primitive fingerprint so the fetch only re-runs when the list of
  // tenant IDs actually changes (auth may hand back a new array reference
  // every render with identical contents).
  const compagniesKey        = compagnies.join('|');
  const [userCompagnies, setUserCompagnies] = useState([]);
  // Superadmin: de volledige omgevingenlijst is pas nodig zodra het menu
  // opengaat. Bij het laden van een pagina halen we alleen de eigen
  // omgevingen op, zodat de knop meteen de juiste naam toont.
  const [allLoaded, setAllLoaded] = useState(false);
  // 'nog niet geladen' is een eigen toestand, los van 'geen resultaten'. Zonder
  // dat onderscheid beweert de knop '-- Selecteer --' terwijl de read nog loopt,
  // en precies hetzelfde wanneer die read geweigerd wordt.
  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  const [ownLoaded, setOwnLoaded]  = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [open, setOpen]      = useState(false);
  const [search, setSearch]  = useState('');
  const containerRef         = useRef(null);

  // Eigen omgevingen (plus de omgeving waar je nu in zit, ook zonder
  // lidmaatschap — anders staat er geen naam op de knop).
  useEffect(() => {
    const ownIds = Array.from(new Set([...compagnies, id].filter(Boolean)));

    if (ownIds.length === 0) {
      setUserCompagnies([]);
      setOwnLoaded(true);
      return;
    }

    let isMounted = true;

    async function fetchOwn() {
      try {
        // Firestore v8 `in` operator accepts max 10 values per query
        const batches = chunk(ownIds, 10);
        const snaps = await Promise.all(
          batches.map(ids =>
            db.collection('CompagnyMeta')
              .where('CompagnyID', 'in', ids)
              .get()
          )
        );
        const docs = snaps.flatMap((snap) => snap.docs.map((doc) => doc.data()));

        if (isMounted) {
          setUserCompagnies(buildAccountList(docs, { currentId: id, memberOf: compagnies }));
        }
      } catch (err) {
        // Niet wegslikken: een mislukte read gaf een LEGE lijst, en dat is niet te
        // onderscheiden van 'je hoort nergens bij'. De gebruiker leest het in de
        // lijst, de ontwikkelaar in de console.
        console.error('CompagnyMeta kon niet worden geladen', err);
        if (isMounted) setLoadFailed(true);
      } finally {
        if (isMounted) setOwnLoaded(true);
      }
    }

    fetchOwn();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compagniesKey, id]);

  // Superadmin: alle omgevingen, pas bij het openen van het menu.
  // CompagnyMeta is publiek leesbaar, dus dit werkt ook zonder lopende sessie
  // — je moet immers kunnen kiezen vóór je er een start.
  // AI-CONTEXT: See docs/features/superadmin-tenant-access.md
  useEffect(() => {
    if (!open || !isSuperAdmin || allLoaded) return;

    let isMounted = true;

    async function fetchAll() {
      try {
        const snap = await db.collection('CompagnyMeta').get();
        const docs = snap.docs.map((doc) => doc.data());

        if (isMounted) {
          setUserCompagnies(buildAccountList(docs, { currentId: id, memberOf: compagnies }));
          setAllLoaded(true);
        }
      } catch (err) {
        console.error('De volledige omgevingenlijst kon niet worden geladen', err);
        if (isMounted) setLoadFailed(true);
      }
    }

    fetchAll();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSuperAdmin, allLoaded, compagniesKey, id]);

  // Close the dropdown when clicking outside of it.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const accountHandler = async (account, isMember) => {
    if (!account) return;

    // Alleen tellen wanneer je er ECHT hoort. Een superadmin die even
    // meekijkt mag de LastLogin van een klant niet oppoetsen — die kolom
    // stuurt het projectenoverzicht aan ("laatste login"), en een slapende
    // omgeving zou er actief door gaan uitzien.
    if (authO?.Docid && isMember) {
      // Dezelfde schrijfactie als op het inlogscherm — vandaar één gedeelde
      // helper in plaats van twee kopieën die uit elkaar gaan lopen.
      // Rechtstreeks naar Firestore, want een Cloud Function start koud op en
      // het navigeren hieronder breekt een lopende aanroep af.
      // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
      await recordLogin({ userDocId: authO.Docid, compagnyId: account });
    }

    // Hard navigate: `client` in src/hooks/Client.js is read once at module load,
    // so SPA-routing between tenants leaves stale tenant state. Full reload resets it.
    window.location.assign(`/${account}/dashboard`);
  };

  // Volgorde van zeker naar onzeker: de naam uit de eigen lijst, anders de naam
  // die de aanroeper al kende, en pas als de lijst ECHT geladen is de mededeling
  // dat er niets gekozen staat.
  const matchedName = userCompagnies.find(c => c.communityID === id)?.communityName;
  const selectedName = matchedName
    || fallbackName
    || (ownLoaded && !loadFailed ? '-- Selecteer --' : '');

  const filteredCompagnies = userCompagnies.filter(c =>
    c.communityName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="switch-account" ref={containerRef}>
      <div
        className="switch-account-selected"
        onClick={() => setOpen(o => !o)}
      >
        <span className="switch-account-selected-text">{selectedName}</span>
        <span className="switch-account-arrow">▼</span>
      </div>

      {open && (
        <div className="switch-account-dropdown">
          <input
            type="text"
            className="switch-account-search"
            placeholder="Zoeken..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="switch-account-options">
            {loadFailed ? (
              <div className="switch-account-empty">De lijst met organisaties kon niet worden geladen</div>
            ) : !ownLoaded ? (
              <div className="switch-account-empty">Bezig met laden…</div>
            ) : filteredCompagnies.length === 0 ? (
              <div className="switch-account-empty">Geen accounts gevonden</div>
            ) : (
              filteredCompagnies.map((c) => (
                <div
                  key={c.communityID}
                  className={`switch-account-option${c.communityID === id ? ' is-active' : ''}`}
                  onClick={() => {
                    setOpen(false);
                    accountHandler(c.communityID, c.isMember);
                  }}
                >
                  {c.communityName}
                  {isSuperAdmin && !c.isMember && (
                    <span className="switch-account-option-badge">sessie nodig</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SwitchAccount;
